import type {
  ApplyRequest,
  ApplyResponse,
  AuditResponse,
  ChangePasswordRequest,
  ClaimPairingRequest,
  LoginRequest,
  MaterialsResponse,
  PluginsResponse,
  RegisterRequest,
  ResourceResponse,
  ServerResponse,
  ServersResponse,
  SessionResponse,
  UpdateServerRequest,
  ValidateRequest,
  ValidateResponse,
} from '@comficonf/shared';
import {
  ApplyResponseSchema,
  AuditResponseSchema,
  MaterialsResponseSchema,
  PluginsResponseSchema,
  ResourceResponseSchema,
  ServerResponseSchema,
  ServersResponseSchema,
  SessionResponseSchema,
  ValidateResponseSchema,
} from '@comficonf/shared';
import type { ApiClient } from './client';

type Signal = AbortSignal | undefined;

function serverPath(serverId: string): string {
  return `/servers/${encodeURIComponent(serverId)}`;
}

function resourcePath(serverId: string, resourceId: string): string {
  return `${serverPath(serverId)}/resources/${encodeURIComponent(resourceId)}`;
}

/** Typed functions for every HTTP API v1 endpoint used by the web app. */
export function createEndpoints(client: ApiClient) {
  return {
    eventsUrl: () => client.url('/events'),

    getSession: (signal?: Signal) =>
      client.request<SessionResponse>('GET', '/auth/session', {
        schema: SessionResponseSchema,
        signal,
        skipAuthRedirect: true,
      }),
    login: (body: LoginRequest) =>
      client.request<SessionResponse>('POST', '/auth/login', {
        body,
        schema: SessionResponseSchema,
        skipCsrf: true,
        skipAuthRedirect: true,
      }),
    register: (body: RegisterRequest) =>
      client.request<SessionResponse>('POST', '/auth/register', {
        body,
        schema: SessionResponseSchema,
        skipCsrf: true,
        skipAuthRedirect: true,
      }),
    logout: () => client.request('POST', '/auth/logout', { skipAuthRedirect: true }),
    changePassword: (body: ChangePasswordRequest) =>
      client.request('POST', '/auth/password', { body }),
    revokeOtherSessions: () => client.request('POST', '/auth/sessions/revoke-others'),

    listServers: (signal?: Signal) =>
      client.request<ServersResponse>('GET', '/servers', { schema: ServersResponseSchema, signal }),
    getServer: (serverId: string, signal?: Signal) =>
      client.request<ServerResponse>('GET', serverPath(serverId), {
        schema: ServerResponseSchema,
        signal,
      }),
    renameServer: (serverId: string, body: UpdateServerRequest) =>
      client.request<ServerResponse>('PATCH', serverPath(serverId), {
        body,
        schema: ServerResponseSchema,
      }),
    removeServer: (serverId: string) => client.request('DELETE', serverPath(serverId)),
    claimPairing: (body: ClaimPairingRequest) =>
      client.request<ServerResponse>('POST', '/pairing/claim', {
        body,
        schema: ServerResponseSchema,
      }),

    listPlugins: (serverId: string, signal?: Signal) =>
      client.request<PluginsResponse>('GET', `${serverPath(serverId)}/plugins`, {
        schema: PluginsResponseSchema,
        signal,
      }),
    getResource: (serverId: string, resourceId: string, signal?: Signal) =>
      client.request<ResourceResponse>('GET', resourcePath(serverId, resourceId), {
        schema: ResourceResponseSchema,
        signal,
      }),
    validate: (serverId: string, resourceId: string, body: ValidateRequest, signal?: Signal) =>
      client.request<ValidateResponse>('POST', `${resourcePath(serverId, resourceId)}/validate`, {
        body,
        schema: ValidateResponseSchema,
        signal,
      }),
    apply: (serverId: string, resourceId: string, body: ApplyRequest) =>
      client.request<ApplyResponse>('POST', `${resourcePath(serverId, resourceId)}/apply`, {
        body,
        schema: ApplyResponseSchema,
      }),
    getMaterials: (serverId: string, signal?: Signal) =>
      client.request<MaterialsResponse>('GET', `${serverPath(serverId)}/catalogs/materials`, {
        schema: MaterialsResponseSchema,
        signal,
      }),
    getAudit: (serverId: string, params: { limit?: number; before?: string }, signal?: Signal) =>
      client.request<AuditResponse>('GET', `${serverPath(serverId)}/audit`, {
        schema: AuditResponseSchema,
        signal,
        query: { limit: params.limit, before: params.before },
      }),
  };
}

export type Endpoints = ReturnType<typeof createEndpoints>;
